import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import Reveal from "./Reveal";

const FAQS = [
  {
    q: "What exactly are surplus foreclosure funds?",
    a: "When a foreclosed home sells at auction for more than the outstanding mortgage and fees, the leftover money — called surplus or excess proceeds — legally belongs to the previous homeowner. Counties hold these funds, but they rarely notify the rightful claimant.",
  },
  {
    q: "Why didn't the county just send me the money?",
    a: "Counties are not required to actively find you. You typically have to file a court petition with detailed documentation, and most states impose strict deadlines (often 1-3 years). Funds left unclaimed are eventually absorbed by the state.",
  },
  {
    q: "What does your service cost?",
    a: "Nothing upfront. We work on a contingency fee — meaning we're only paid a percentage if and when we successfully recover funds for you. Our fee is clearly disclosed in writing before any work begins.",
  },
  {
    q: "How long does the recovery process take?",
    a: "Most cases resolve in 60-180 days depending on the county, court schedules, and any competing claims. We keep you informed at every milestone.",
  },
  {
    q: "I lost my home years ago — is it too late?",
    a: "Maybe not. Different states have different statute periods. Some allow 3-5+ years to file. The only way to know is to let us check the records — that part is always free.",
  },
  {
    q: "Is this a scam? I've gotten suspicious letters before.",
    a: "Surplus funds recovery is a legitimate, regulated service — but it does attract scammers who demand upfront fees or pressure you to sign immediately. We never ask for money upfront, and you're welcome to verify our credentials before doing anything.",
  },
  {
    q: "What documents do you need from me?",
    a: "Typically: a government-issued ID, the foreclosure case number (we can find this for you), and a signed authorization to file on your behalf. We guide you through every form.",
  },
];

export default function FAQ() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a,
      },
    })),
  };

  return (
    <section id="faq" className="py-20 sm:py-24 lg:py-32 bg-[#F7F5F0]" data-testid="faq-section">
      <script
        type="application/ld+json"
        data-testid="faq-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <div className="max-w-7xl mx-auto px-6">
        {/* Section heading at the top */}
        <Reveal>
          <div className="max-w-2xl mb-12 sm:mb-16">
            <p className="text-sm sm:text-base font-bold uppercase tracking-[0.18em] text-[#FF008F] mb-4">Frequently Asked</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-tight">
              Honest answers, no legalese.
            </h2>
            <p className="mt-4 text-[#475569] leading-relaxed">
              Have a question we didn't cover? Our recovery specialists are one phone call away — bilingual support available.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left: Photo bleeding into background */}
          <Reveal className="lg:col-span-5">
            <div className="flex flex-col items-center">
              {/* Photo - rounded card */}
              <div className="relative w-full max-w-[440px] aspect-[4/5] rounded-3xl overflow-hidden border border-[#E2DDD3] shadow-xl shadow-[#0F172A]/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/4fsbfhne_Erika%20Escobar2.jpeg"
                  alt="Erika Escobar — Vice President, American Asset Recovery Agency"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </div>

              {/* Quote + identity below photo */}
              <div className="mt-6 text-center max-w-md">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF008F]">
                  Bilingual Support
                </p>
                <p className="mt-3 font-serif-display text-xl sm:text-2xl text-[#0F172A] leading-snug italic">
                  "We answer every question — in English or Spanish. It's our promise to you."
                </p>
                <p className="mt-6 font-serif-display text-xl text-[#0F172A] font-semibold">
                  Erika Escobar
                </p>
                <p className="text-sm text-[#475569] tracking-wide mt-1">
                  Vice President
                </p>
              </div>
            </div>
          </Reveal>

          {/* Right: FAQ accordion */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <Reveal key={f.q} delay={i * 0.05}>
                  <AccordionItem value={`item-${i}`} className="bg-white rounded-2xl border border-[#E2DDD3] px-5 sm:px-6 shadow-sm" data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left font-serif-display text-base sm:text-lg text-[#0F172A] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#475569] leading-relaxed pb-5">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                </Reveal>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
